import { ButtonGroup } from "@mui/material";
import React, { useState, useRef, useContext, useEffect } from "react";
import { Link } from "react-router-dom";

import { useTranslation } from "react-i18next";
import { Results } from "../../pages/Results";
import { usePage } from "../../routes";

import {
  DataTable,
  DataTableMultiSortMetaType,
  DataTableSortOrderType,
  DataTableSortParams,
} from "primereact/datatable";
import { Column } from "primereact/column";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartLine, faPlus } from "@fortawesome/free-solid-svg-icons";
import { Row, Col } from "react-bootstrap";
import Button from "@mui/material/Button";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { TextTemplate } from "../shared/Grid/BodyTemplates";
import { GlobalFilter } from "../shared/Grid/GlobalFilterTemplate";
import { Asset } from "../shared/interfaces/Asset";
import { HvSubstationViewer } from "./HvSubstationViewer";
import RefreshLoadingButton from "../shared/RefreshLoadingButton";
import DataTableSkeleton from "../shared/DataTableSkeleton";
import { HvSubstation } from "../shared/interfaces/HvSubstation";
import { HvSubstationMetadata } from "../shared/interfaces/HvSubstationMetadata";
import { useAlternativeRegionNames } from "../data/Settings/useAlternativeRegionNames";
import { LOCALSTORAGE_KEYS } from "../shared/constants";
import { AccessControlContext } from "../contexts/AccessControlContext";

interface Props {
  customerSelected: boolean;
  hvSubstationData?: HvSubstationMetadata[];
  hvSubstationDataLoading: boolean;
  hvSubstationDataFetching: boolean;
  onDataRefreshClick: () => void;
  onNewHvSubstationClick: () => void;
}

const sortFieldStorageKey = LOCALSTORAGE_KEYS.hvSubstationSortField;
const sortOrderStorageKey = LOCALSTORAGE_KEYS.hvSubstationSortOrder;

export function HvSubstationGrid(props: Props) {
  const { t } = useTranslation();
  const dt = useRef<DataTable>(null);
  const hvSubstationViewerPath = usePage(HvSubstationViewer.name)?.path;
  const resultsPath = usePage(Results.name)?.path;

  const altRegionNames = useAlternativeRegionNames(props.hvSubstationData?.at(0)?.customerId);

  const accessControlContext = useContext(AccessControlContext);
  const userCanAddHvSubstation = accessControlContext.canI(
    "add",
    "hvSubstations",
    props.hvSubstationData?.at(0)?.customerId,
  );

  const [globalFilter, setGlobalFilter] = useState<string>("");

  function handleGlobalFilterInput(event: React.FormEvent<HTMLInputElement>): void {
    const element = event.target as HTMLInputElement;
    setGlobalFilter(element.value);
  }

  const [multiSortMeta, setMultiSortMeta] = useState<DataTableMultiSortMetaType>();

  const onSort = (e: DataTableSortParams) => {
    const field = e.multiSortMeta?.at(0)?.field as string;
    const order = e.multiSortMeta?.at(0)?.order;
    localStorage.setItem(sortFieldStorageKey, field);
    order && localStorage.setItem(sortOrderStorageKey, order?.toString());
    const sort: DataTableMultiSortMetaType = [{ field: field, order: order }];
    setMultiSortMeta(sort);
  };

  useEffect(() => {
    const field = localStorage.getItem(sortFieldStorageKey) as string;
    const order = localStorage.getItem(sortOrderStorageKey) as DataTableSortOrderType;
    const sort: DataTableMultiSortMetaType = [{ field: field, order: order }];
    setMultiSortMeta(sort);
  }, [props]);

  const headerToolbar = (
    <Row>
      <Col>
        {props.customerSelected && userCanAddHvSubstation && (
          <Button
            variant="contained"
            color="primary"
            onClick={props.onNewHvSubstationClick}
            startIcon={<FontAwesomeIcon style={{ cursor: "pointer" }} icon={faPlus} />}
          >
            {t("substations.hvSubstation")}
          </Button>
        )}
      </Col>
      <Col className="d-flex justify-content-center">
        <GlobalFilter onInput={handleGlobalFilterInput} />
      </Col>
      <Col className="d-flex justify-content-end">
        <RefreshLoadingButton isLoading={props.hvSubstationDataFetching} onClick={props.onDataRefreshClick} />
      </Col>
    </Row>
  );

  const actionBodyTemplate = (rowData: Asset) => {
    return (
      <ButtonGroup variant="contained">
        <Button
          variant="contained"
          color="primary"
          aria-label={t("common.view")}
          component={Link}
          to={{
            pathname: hvSubstationViewerPath?.replace(":assetId", rowData.id.toString()),
          }}
        >
          <VisibilityIcon />
        </Button>

        <Button
          variant="contained"
          color="primary"
          aria-label={t("results.results")}
          component={Link}
          to={{
            pathname: resultsPath + `?substation=${encodeURIComponent(rowData.name)}`,
          }}
        >
          <FontAwesomeIcon icon={faChartLine} />
        </Button>
      </ButtonGroup>
    );
  };
  if (props.hvSubstationDataLoading) return <DataTableSkeleton />;
  else
    return (
      <React.Fragment>
        <div className="datatable-responsive">
          <DataTable
            ref={dt}
            value={props.hvSubstationData}
            header={headerToolbar}
            // If the global filter is an empty string add a space, otherwise some results may get filtered out
            globalFilter={globalFilter === "" ? " " : globalFilter}
            filterDisplay="row"
            removableSort
            sortMode="multiple"
            className="p-datatable-responsive p-datatable-gridlines p-datatable-sm"
            emptyMessage={t("substations.emptyMessage")}
            loading={props.hvSubstationDataLoading}
            multiSortMeta={multiSortMeta}
            onSort={onSort}
          >
            <Column
              field={"name"}
              header={t("substations.substationName")}
              body={(rowData: HvSubstation) => (
                <TextTemplate rowData={rowData} field={"name"} header={t("substations.substationName")} />
              )}
              filter
              showFilterMenu={false}
              showClearButton={false}
              sortable
              sortField="name"
            />
            <Column
              field={"serialNumber"}
              header={t("substations.substationNumber")}
              body={(rowData: HvSubstation) => (
                <TextTemplate rowData={rowData} field={"serialNumber"} header={t("substations.substationNumber")} />
              )}
              filter
              showFilterMenu={false}
              showClearButton={false}
              sortable
              sortField="serialNumber"
            />
            <Column
              field={"company"}
              header={altRegionNames.data?.companyAltName ?? t("common.company")}
              body={(rowData: HvSubstation) => (
                <TextTemplate
                  rowData={rowData}
                  field={"company"}
                  header={altRegionNames.data?.companyAltName ?? t("common.company")}
                />
              )}
              filter
              showFilterMenu={false}
              showClearButton={false}
              sortable
              sortField="company"
            />
            <Column
              field={"site"}
              header={altRegionNames.data?.siteAltName ?? t("common.site")}
              body={(rowData: HvSubstation) => (
                <TextTemplate
                  rowData={rowData}
                  field={"site"}
                  header={altRegionNames.data?.siteAltName ?? t("common.site")}
                />
              )}
              filter
              showFilterMenu={false}
              showClearButton={false}
              sortable
              sortField="site"
            />
            <Column
              body={actionBodyTemplate}
              headerStyle={{ width: "8em", textAlign: "center" }}
              bodyStyle={{ textAlign: "center", overflow: "visible" }}
              header={t("common.actions")}
            />
          </DataTable>
        </div>
      </React.Fragment>
    );
}
